// PrivateShowGift.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import numberWithCommas from '../resource/numberWithCommas.js';
import {
  privateShowGiftType,
  privateShowStatus,
} from '../resource/liveStreamConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { color, textColor } from '../style/variables.js';
import TextEllipsis from '../style/TextEllipsis.js';
import DiamondIconSrc from '../../img/point_2.svg';
import TicketMoreIconSrc from '../../img/img_show_ticket_more.svg';
import TicketMVPIconSrc from '../../img/img_show_ticket_mvp.svg';
import TicketSingleIconSrc from '../../img/img_show_ticket_single.svg';

export class PrivateShowGift extends React.PureComponent {
  renderHeader = () => {
    const { status, type, isSelected, t } = this.props;
    if (type === privateShowGiftType.SINGLE) {
      return <Header shouldHide isSelected={isSelected} />;
    }
    if (type === privateShowGiftType.MVP) {
      return (
        <Header isSelected={isSelected}>
          <HeaderText>
            {t('show_become_mvp', {
              ns: TranslationNamespace.LIVESTREAM,
            })}
          </HeaderText>
        </Header>
      );
    }
    if (type === privateShowGiftType.MORE) {
      if (
        [privateShowStatus.PREPARING, privateShowStatus.PERFORMING].includes(
          status
        )
      ) {
        return (
          <Header isSelected={isSelected}>
            <HeaderText>
              {t('show_strength', {
                ns: TranslationNamespace.LIVESTREAM,
              })}
            </HeaderText>
          </Header>
        );
      }
      return (
        <Header isSelected={isSelected}>
          <HeaderText>
            {t('popup_achieve_target', {
              ns: TranslationNamespace.LIVESTREAM,
            })}
          </HeaderText>
        </Header>
      );
    }
    return null;
  };
  getEarlyBirdDiscountPercentage = () => {
    const {
      earlyBirdDiscountPercentage,
      remoteConfigEarlyBirdPrice,
      remoteConfigTicketPrice,
    } = this.props;
    if (earlyBirdDiscountPercentage !== null)
      return earlyBirdDiscountPercentage;
    if (remoteConfigEarlyBirdPrice !== null && remoteConfigTicketPrice !== null)
      return Math.floor(
        (1 - remoteConfigEarlyBirdPrice / remoteConfigTicketPrice) * 100
      );
    return 0;
  };
  getShouldShowDiscount = () => {
    const { isEarlyBirdTicket } = this.props;
    return isEarlyBirdTicket && this.getEarlyBirdDiscountPercentage() > 0;
  };
  renderPrice = ({ isOriginalPrice = false } = {}) => {
    const {
      isAuthed,
      price,
      count,
      earlyBirdDiscountPercentage,
      remoteConfigTicketPrice,
    } = this.props;
    return isAuthed ? (
      <>
        <GiftIcon src={DiamondIconSrc} isActive={!isOriginalPrice} />
        <GiftTotalPrice isActive={!isOriginalPrice}>
          {price
            ? numberWithCommas({
                number: isOriginalPrice
                  ? earlyBirdDiscountPercentage !== null
                    ? Math.ceil(
                        (price * 100) / (100 - earlyBirdDiscountPercentage)
                      ) * count
                    : remoteConfigTicketPrice * count
                  : price * count,
              })
            : '-'}
        </GiftTotalPrice>
      </>
    ) : null;
  };
  getGiftIconSource = () => {
    const { type } = this.props;
    switch (type) {
      case privateShowGiftType.SINGLE:
        return TicketSingleIconSrc;
      case privateShowGiftType.MORE:
        return TicketMoreIconSrc;
      case privateShowGiftType.MVP:
        return TicketMVPIconSrc;
      default:
        return null;
    }
  };
  fetchGift = () => {
    const { giftId, fetchGift, isGiftFetched } = this.props;
    if (giftId && !isGiftFetched) {
      return fetchGift({ giftId });
    }
  };
  componentDidMount() {
    this.fetchGift();
  }
  componentDidUpdate(prevProps) {
    const { giftId, isAuthed } = this.props;
    if (isAuthed !== prevProps.isAuthed || giftId !== prevProps.giftId) {
      this.fetchGift();
    }
  }
  render() {
    const { count, isSelected, t } = this.props;
    return (
      <StyledPrivateShowGift isSelected={isSelected}>
        {this.renderHeader()}
        <Body>
          <GiftImage src={this.getGiftIconSource()} />
          <GiftName>
            {count
              ? t('show_tickets', {
                  number: numberWithCommas({ number: count }),
                  ns: TranslationNamespace.LIVESTREAM,
                })
              : '-'}
          </GiftName>
          <Price>{this.renderPrice()}</Price>
          {this.getShouldShowDiscount() && (
            <OriginalPrice>
              {this.renderPrice({ isOriginalPrice: true })}
            </OriginalPrice>
          )}
          {this.getShouldShowDiscount() && (
            <Badge>
              <BadgeText>
                {this.getEarlyBirdDiscountPercentage()}% Off
              </BadgeText>
            </Badge>
          )}
        </Body>
      </StyledPrivateShowGift>
    );
  }
}

PrivateShowGift.propTypes = {
  giftId: PropTypes.string,
  status: PropTypes.string,
  type: PropTypes.string,
  count: PropTypes.number,
  remoteConfigEarlyBirdPrice: PropTypes.number,
  remoteConfigTicketPrice: PropTypes.number,
  price: PropTypes.number,
  earlyBirdDiscountPercentage: PropTypes.number,
  isSelected: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isEarlyBirdTicket: PropTypes.bool,
  isGiftFetched: PropTypes.bool,
  fetchGift: PropTypes.func,
  t: PropTypes.func.isRequired,
};

PrivateShowGift.defaultProps = {
  giftId: '',
  status: null,
  type: null,
  count: null,
  remoteConfigEarlyBirdPrice: null,
  remoteConfigTicketPrice: null,
  price: null,
  earlyBirdDiscountPercentage: null,
  isSelected: false,
  isAuthed: false,
  isEarlyBirdTicket: false,
  isGiftFetched: false,
  fetchGift: () => null,
};

const StyledPrivateShowGift = styled.div`
  border-radius: 4px;
  ${({ isSelected }) => {
    if (isSelected) {
      return css`
        border: 2px solid ${color.carmine};
      `;
    }
  }}
`;

const Header = styled.div`
  padding-left: 4px;
  padding-right: 4px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ isSelected, shouldHide }) => {
    if (isSelected && !shouldHide) {
      return css`
        background-color: ${color.carmine};
      `;
    }
  }}
  overflow: hidden;
  line-height: 1;
`;

const HeaderText = styled.div`
  ${TextEllipsis};
  font-size: 12px;
  font-weight: 600;
  color: ${textColor.white};
`;

const Body = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GiftImage = styled.img`
  margin-bottom: 4px;
  width: 72px;
  height: 72px;
  object-fit: cover;
`;

const GiftName = styled.div`
  margin-bottom: 8px;
  font-size: 12px;
  line-height: 1;
  color: ${textColor.white};
  text-align: center;
`;

const Badge = styled.div`
  margin-top: 4px;
  padding: 0px 4px;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${color.red};
`;

const BadgeText = styled.div`
  ${TextEllipsis};
  font-size: 12px;
  line-height: 1.5;
  color: ${color.neutral[100]};
`;

const Price = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 21px;
`;

const OriginalPrice = styled(Price)`
  position: relative;
  ::after {
    position: absolute;
    left: 0px;
    right: 0px;
    top: 50%;
    height: 1px;
    background-color: ${color.red};
    transform: translateY(-50%);
    content: '';
  }
`;

const InactiveStyle = css`
  opacity: ${({ isActive }) => (isActive ? 1 : 0.6)};
`;

const GiftIcon = styled.img`
  ${InactiveStyle};
  margin-right: 4px;
  width: 12px;
  height: 12px;
`;

const GiftTotalPrice = styled.div`
  ${InactiveStyle};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: ${textColor.primary};
`;

export default withTranslation()(PrivateShowGift);
